import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { sound } from "@pixi/sound";
import { BitmapText, Container, Graphics, Sprite, Text } from "pixi.js";
import { Tween } from "tweedle.js";
import { isSuperQuickBtnOnGlobal, languageGlobal } from "../../config/GlobalVariableConfig";
import { getFontConfig } from "../../config/introTextConfig";
import { DisplayObject, IDisplayObject } from "../../core/displays/DisplayObject";
import {
    createUpgradeTitleStyle,
    INTRO_BANNER_LINE_GAP,
    INTRO_BANNER_PART_GAP,
    JACKPOT_UPGRADE_AMOUNT_FONT_SIZE,
    JACKPOT_UPGRADE_TITLE_FONT_SIZE,
    layoutHorizontalParts,
    layoutIntroBannerText,
    parseJackpotIntroDesc,
} from "../../core/jackpotUpgradeText";
import { wait } from "../../core/utils";
import { GameConstants } from "../../gameClientEvents/GameConstants";
import { SoundConstants } from "../../gameClientEvents/SoundConstants";

declare const window: any;
const translationManager = window.hacksawCasino.Translation;

export class JackpotIntroBanner extends DisplayObject {
    private textContainer!: Container;
    private line1Container!: Container;
    private line3Container!: Container;

    private collectLabel!: Text;
    private scarabCountText!: BitmapText;
    private scarabIcon!: Sprite;
    private upgradeLine!: Text;
    private freespinsPrefix!: Text;
    private respinsCountText!: BitmapText;
    private freespinsSuffix!: Text;

    private blackLayer: Graphics = new Graphics();

    private titleFontSize = JACKPOT_UPGRADE_TITLE_FONT_SIZE;
    private amountFontSize = JACKPOT_UPGRADE_AMOUNT_FONT_SIZE;

    private lastRespins = 3;
    private lastScarabCount = 0;

    constructor(config: IDisplayObject) {
        super(config);
        this.init();
        this.visible = false;
    }

    private init(): void {
        this.setupOverlay();
        this.setupText();
    }

    private setupOverlay(): void {
        this.blackLayer
            .rect(-10000, -10000, 20000, 20000)
            .fill(0x000000);

        this.blackLayer.alpha = 0.65;
        this.blackLayer.visible = false;

        this.addChild(this.blackLayer);
    }

    private setupText(): void {
        const {
            fontFamily: fontFamilyHeading,
            fontWeight: fontWeightHeading,
        } = getFontConfig(languageGlobal, "jackpotHeading");

        this.textContainer = new Container();
        this.textContainer.label = "jackpotIntroBannerContent";
        this.textContainer.position.set(
            GameConstants.BACKGROUND_SIZE / 2,
            GameConstants.BACKGROUND_SIZE / 2
        );
        this.textContainer.visible = false;

        this.addChild(this.textContainer);

        this.line1Container = new Container();
        this.line3Container = new Container();

        this.collectLabel = new Text({
            text: "",
            style: createUpgradeTitleStyle(fontFamilyHeading, fontWeightHeading, this.titleFontSize),
        });
        this.collectLabel.anchor.set(0.5);

        this.scarabCountText = new BitmapText({
            text: "",
            style: {
                fontFamily: "scarab_meter_default",
                fontSize: this.amountFontSize,
                align: "center",
            },
        });
        this.scarabCountText.anchor.set(0.5);

        this.scarabIcon = Sprite.from("beetle");
        this.scarabIcon.anchor.set(0.5);

        this.line1Container.addChild(this.collectLabel, this.scarabCountText, this.scarabIcon);

        this.upgradeLine = new Text({
            text: "",
            style: createUpgradeTitleStyle(fontFamilyHeading, fontWeightHeading, this.titleFontSize),
        });
        this.upgradeLine.anchor.set(0.5);

        this.freespinsPrefix = new Text({
            text: "",
            style: createUpgradeTitleStyle(fontFamilyHeading, fontWeightHeading, 60),
        });
        this.freespinsPrefix.anchor.set(0.5);

        this.respinsCountText = new BitmapText({
            text: "",
            style: {
                fontFamily: "scarab_meter_default",
                fontSize: this.amountFontSize,
                align: "center",
            },
        });
        this.respinsCountText.anchor.set(0.5);

        this.freespinsSuffix = new Text({
            text: "",
            style: createUpgradeTitleStyle(fontFamilyHeading, fontWeightHeading, 60),
        });
        this.freespinsSuffix.anchor.set(0.5);

        this.line3Container.addChild(this.freespinsPrefix, this.respinsCountText, this.freespinsSuffix);

        this.textContainer.addChild(this.line1Container, this.upgradeLine, this.line3Container);
    }

    private setBannerTextContent(numOfRespins: number, scarabCount: number): void {
        const translated = translationManager.game("jackpot_intro_desc", null);
        const { collect, upgrade, youhave, freespins } = parseJackpotIntroDesc(translated);

        this.collectLabel.text = collect;
        this.scarabCountText.text = `${scarabCount}`;
        this.upgradeLine.text = upgrade;

        this.freespinsPrefix.text = youhave;
        this.respinsCountText.text = `${numOfRespins}`;
        this.freespinsSuffix.text = freespins;

        const iconHeight = this.scarabCountText.height * 1.15;
        this.scarabIcon.scale.set(iconHeight / this.scarabIcon.texture.height);

        layoutHorizontalParts(
            [this.collectLabel, this.scarabCountText, this.scarabIcon],
            INTRO_BANNER_PART_GAP,
            0
        );
        layoutHorizontalParts(
            [this.freespinsPrefix, this.respinsCountText, this.freespinsSuffix],
            INTRO_BANNER_PART_GAP,
            0
        );
        layoutIntroBannerText(
            this.line1Container,
            this.upgradeLine,
            this.line3Container,
            INTRO_BANNER_LINE_GAP
        );
    }

    private fitTextToWidth(maxWidth: number): void {
        while (
            this.line1Container.width > maxWidth
            || this.upgradeLine.width > maxWidth
            || this.line3Container.width > maxWidth
        ) {
            this.titleFontSize -= 1;
            this.amountFontSize -= 1;
            this.collectLabel.style.fontSize = this.titleFontSize;
            this.upgradeLine.style.fontSize = this.titleFontSize;
            this.freespinsPrefix.style.fontSize = this.titleFontSize;
            this.freespinsSuffix.style.fontSize = this.titleFontSize;
            this.scarabCountText.style.fontSize = this.amountFontSize;
            this.respinsCountText.style.fontSize = this.amountFontSize;
            this.setBannerTextContent(this.lastRespins, this.lastScarabCount);
        }
    }

    private resetFontSizes(): void {
        this.titleFontSize = JACKPOT_UPGRADE_TITLE_FONT_SIZE;
        this.amountFontSize = JACKPOT_UPGRADE_AMOUNT_FONT_SIZE;
        this.collectLabel.style.fontSize = this.titleFontSize;
        this.upgradeLine.style.fontSize = this.titleFontSize;
        this.freespinsPrefix.style.fontSize = 60;
        this.freespinsSuffix.style.fontSize = 60;
        this.scarabCountText.style.fontSize = this.amountFontSize;
        this.respinsCountText.style.fontSize = this.amountFontSize;
    }

    public playBannerAnimSequence(numOfRespins: number, scarabCount: number, callback: () => void): void {
        this.lastRespins = numOfRespins;
        this.lastScarabCount = scarabCount;

        this.resetFontSizes();
        this.setBannerTextContent(numOfRespins, scarabCount);
        this.fitTextToWidth(1000);

        const dustAnim = Spine.from({
            skeleton: "bannerDustSkel",
            atlas: "bannerDustAtlas",
        });
        dustAnim.label = "jackpotIntroBannerDust";
        dustAnim.position.set(
            GameConstants.BACKGROUND_SIZE / 2 - 275,
            GameConstants.BACKGROUND_SIZE / 2
        );
        this.addChild(dustAnim);
        dustAnim.state.setAnimation(0, "in", false);
        dustAnim.state.addAnimation(0, "loop", true, 0);

        this.visible = true;
        this.blackLayer.visible = true;
        this.textContainer.visible = true;

        const targetScale = Math.min(950 / this.textContainer.width, 1);

        this.textContainer.scale.set(0.01);
        sound.play(SoundConstants.JACKPOT_UPGRADED, { volume: 0.8 });
        new Tween(this.textContainer.scale)
            .to({ x: targetScale, y: targetScale }, 200)
            .start()
            .onComplete(async () => {
                await (isSuperQuickBtnOnGlobal ? wait(1000) : wait(2000));

                dustAnim.state.setAnimation(0, "out", false);
                dustAnim.state.addListener({
                    complete: () => {
                        dustAnim.destroy({ children: true });
                        this.visible = false
                    },
                });

                new Tween(this.textContainer.scale)
                    .to({ x: 0, y: 0 }, 200)
                    .start()
                    .onComplete(() => {
                        this.blackLayer.visible = false;
                        this.textContainer.visible = false;
                        // this.visible = false;
                        this.textContainer.scale.set(1);
                        callback?.();
                    });
            });
    }
}
